import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AriaOrb } from "../components/AriaOrb";
import { MessageSquare, Mic, Brain, ShieldCheck, Sparkles, Quote, ArrowRight, Youtube, BookOpen, FileText, Network } from "lucide-react";
import { api, saveToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const features = [
  { icon: Network, title: "One knowledge system", text: "Lectures, notes, TurboLearn imports, and research papers all feed a single connected model of what you know." },
  { icon: MessageSquare, title: "Source-aware answers", text: "Every grounded answer cites exactly where it came from — a lecture timestamp, a note, an import, a paper." },
  { icon: Mic, title: "Voice in & out", text: "Speak naturally; ARIA listens, transcribes, and reads back — hands-free study and review." },
  { icon: Brain, title: "Real mastery tracking", text: "Concept mastery (0-5) is built from evidence — quizzes, interviews, study sessions — never guessed." },
  { icon: ShieldCheck, title: "Private by design", text: "Single-user, encrypted in transit, exportable, deletable. Nothing here is shared or trained on." },
  { icon: Sparkles, title: "Interview-ready", text: "Adaptive mock interviews surface exactly what you don't understand yet — and close the gap on the spot." },
];

const voices = [
  { quote: "It found the exact lecture timestamp I half-remembered, then connected it to my own notes. That's the whole point.", name: "From a study session", role: "Thermodynamics review" },
  { quote: "The interview asked a follow-up I couldn't answer, taught it in two sentences, then asked again. I actually got it.", name: "From a mock interview", role: "Gap closed on Chemical Potential" },
  { quote: "I stopped hunting through three different apps for the same idea. It's one system now.", name: "From a research session", role: "Protein folding literature" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [demoBusy, setDemoBusy] = useState(false);

  const enterPlatform = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: "admin@demo.com", password: "Judysanjay@100" });
      saveToken(data.token);
      await checkAuth();
      toast.success("Welcome back, Dr. Shankar");
      navigate("/app/home");
    } catch (e) {
      toast.error("Unable to sign you in — please try again.");
      setDemoBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--aria-bg)] text-[var(--aria-text)]">
      {/* Nav */}
      <header className="sticky top-0 z-40 aria-glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AriaOrb size={32} />
            <span className="font-display text-xl tracking-tight">ARIA</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="#how" className="px-3 py-2 text-sm rounded-md hover:bg-[var(--aria-surface-2)] hidden md:inline transition-colors">How it works</a>
            <a href="#voices" className="px-3 py-2 text-sm rounded-md hover:bg-[var(--aria-surface-2)] hidden md:inline transition-colors">From sessions</a>
            <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm rounded-md hover:bg-[var(--aria-surface-2)] transition-colors">Sign in</Link>
            <button onClick={enterPlatform} data-testid="landing-try-demo" disabled={demoBusy}
              className="btn btn-primary disabled:opacity-50 flex items-center gap-1.5 text-sm">
              {demoBusy ? "Opening…" : "Enter ARIA"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--aria-border)]">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-grad-deep opacity-70" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-in-stagger">
          <div className="flex items-center gap-3 mb-7">
            <div className="aria-pill aria-pill-primary">Private · Single-user intelligence system</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[5.5rem] tracking-[-0.045em] leading-[1.0] mb-7 font-medium">
            Meet <span className="text-grad-brand">ARIA</span>.<br/>
            Your academic<br/>intelligence<br/>layer.
          </h1>
          <p className="text-lg text-[var(--aria-text-muted)] max-w-xl mb-9 leading-relaxed">
            One intelligence across everything you learn — lectures, notes, research, and interviews. ARIA connects what you watch, read, and write into a single evolving model of what you know and what still needs work.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={enterPlatform} data-testid="hero-cta-demo" disabled={demoBusy}
              className="btn btn-primary px-6 py-3 disabled:opacity-50 flex items-center gap-2 ring-glow-primary">
              {demoBusy ? "Opening…" : "Enter ARIA"} <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate("/signup")} data-testid="hero-cta-signup"
              className="btn px-6 py-3 rounded-sm border border-[var(--aria-border-strong)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)] font-medium transition-colors">
              Create your workspace
            </button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-[var(--aria-text-muted)] font-mono flex-wrap">
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[var(--aria-primary)]" /> Private, single-user</div>
            <div className="flex items-center gap-1.5"><Network size={14} className="text-[var(--aria-primary)]" /> One knowledge graph</div>
            <div className="flex items-center gap-1.5"><Sparkles size={14} className="text-[var(--aria-accent)]" /> Claude Haiku 4.5</div>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -top-8 -left-8 w-56 h-56 rounded-full bg-[var(--aria-primary)]/20 blur-3xl" aria-hidden />
          <div className="aria-card p-8 relative">
            <div className="flex items-center justify-center gap-6 mb-8">
              {[Youtube, BookOpen, FileText].map((Icon, i) => (
                <div key={i} className="w-14 h-14 rounded-full border border-[var(--aria-border-strong)] bg-[var(--aria-surface-2)] flex items-center justify-center">
                  <Icon size={22} className="text-[var(--aria-text-muted)]" />
                </div>
              ))}
            </div>
            <div className="flex justify-center mb-8">
              <AriaOrb size={72} state="thinking" />
            </div>
            <div className="flex items-center justify-center gap-8 text-xs uppercase tracking-[0.15em] text-[var(--aria-text-muted)] font-mono">
              <span>Learning</span><span className="text-[var(--aria-primary)]">→</span>
              <span>Research</span><span className="text-[var(--aria-primary)]">→</span>
              <span>Interviews</span>
            </div>
          </div>
          <div className="absolute -bottom-7 -right-6 aria-card p-4 flex items-center gap-3 bg-[var(--aria-surface)] border border-[var(--aria-border-strong)]">
            <AriaOrb size={42} state="listening" />
            <div>
              <div className="text-sm font-medium">Gap closed.</div>
              <div className="text-xs text-[var(--aria-text-muted)] italic">Chemical Potential · Understanding → Application</div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-[var(--aria-border)] bg-[var(--aria-surface)]/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-start mb-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--aria-primary)] font-semibold mb-3">How it works</div>
              <h2 className="font-display text-3xl lg:text-5xl leading-tight">Everything you learn,<br/>one connected system.</h2>
            </div>
            <div className="lg:col-span-7 text-lg text-[var(--aria-text-muted)] leading-relaxed">
              Connect a YouTube lecture, a TurboLearn export, or your own notes. ARIA extracts real concepts, links them into a knowledge graph, and tracks your mastery from evidence — not guesses. Ask anything, and it answers from your material first.
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", title: "Connect", icon: Youtube, text: "Paste a lecture URL, import TurboLearn material, or add notes. ARIA extracts concepts automatically." },
              { n: "02", title: "Ask", icon: MessageSquare, text: "Ask anything — ARIA searches your connected knowledge first, then reasons over it with Claude Haiku 4.5." },
              { n: "03", title: "Close gaps", icon: Brain, text: "Mock interviews surface exactly what's weak, teach it on the spot, and update your mastery from real evidence." },
            ].map((s) => (
              <div key={s.n} className="aria-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-display text-3xl text-[var(--aria-primary)]">{s.n}</div>
                  <s.icon size={20} className="text-[var(--aria-text-muted)]" />
                </div>
                <h3 className="font-display text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-[var(--aria-text-muted)] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="font-display text-3xl lg:text-4xl mb-12 max-w-xl">Your academic chief of staff, in one screen.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <div key={f.title} className="aria-card p-6" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-10 h-10 rounded-md bg-[var(--aria-primary)]/10 flex items-center justify-center mb-4">
                <f.icon size={20} className="text-[var(--aria-primary)]" />
              </div>
              <h3 className="font-display text-xl mb-2">{f.title}</h3>
              <p className="text-sm text-[var(--aria-text-muted)] leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Voices */}
      <section id="voices" className="border-t border-[var(--aria-border)] bg-[#050912] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-xs uppercase tracking-[0.25em] text-[var(--aria-primary-glow)] font-semibold mb-3">From sessions</div>
          <h2 className="font-display text-3xl lg:text-5xl mb-12 max-w-2xl">The moments ARIA is built for.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {voices.map((v, i) => (
              <div key={i} className="border-l-2 border-[var(--aria-primary)] pl-5 py-2">
                <Quote size={20} className="text-[var(--aria-primary)] mb-3" />
                <p className="text-base leading-relaxed mb-5">{v.quote}</p>
                <div className="text-sm font-medium">{v.name}</div>
                <div className="text-xs text-slate-400">{v.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 text-center">
        <div className="flex justify-center mb-6">
          <AriaOrb size={64} state="idle" />
        </div>
        <h2 className="font-display text-4xl lg:text-5xl mb-5 leading-tight">Stop switching apps.<br/>Start asking ARIA.</h2>
        <p className="text-lg text-[var(--aria-text-muted)] max-w-xl mx-auto mb-8">
          Open ARIA and connect what you're learning. Your knowledge graph, your mastery, your interview prep — all one system.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <button onClick={enterPlatform} disabled={demoBusy} data-testid="footer-cta-demo"
            className="px-6 py-3 rounded-md bg-[var(--aria-primary)] text-white hover:bg-[var(--aria-primary-hover)] font-medium disabled:opacity-50">
            {demoBusy ? "Opening…" : "Enter ARIA"}
          </button>
          <Link to="/signup" data-testid="footer-cta-signup"
            className="px-6 py-3 rounded-md border border-[var(--aria-border)] hover:bg-white/60 dark:hover:bg-white/5 font-medium">
            Create your workspace
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--aria-border)] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap justify-between gap-4 text-sm text-[var(--aria-text-muted)]">
          <div>© {new Date().getFullYear()} ARIA · A private academic intelligence system.</div>
          <div className="flex items-center gap-2"><AriaOrb size={18} /> Learn. Connect. Understand.</div>
        </div>
      </footer>
    </div>
  );
}
